import type { BriefingExport } from "../types";

export interface SendResult {
  ok: boolean;
  /** HTTP status of the route response; 0 when the request never arrived. */
  status: number;
  /**
   * Technical diagnostic, never a user-facing sentence: "Slack returned 403:
   * invalid_token.", "HTTP 502", "Failed to fetch". Always populated on failure
   * — a revoked webhook and a dropped connection look identical without it.
   */
  error?: string;
}

/**
 * Opt-in transmission to DBI (Slack). Only ever called after the user ticks the
 * consent box — downloading the PDF/JSON never touches this.
 */
export async function sendBriefing(payload: BriefingExport, endpoint: string): Promise<SendResult> {
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, gdprConsent: true }),
    });
    if (response.ok) return { ok: true, status: response.status };
    // A gateway or CDN can fail this with an HTML body, so `error` may be
    // missing — fall back to the status rather than reporting a blank reason.
    const body = await response.json().catch(() => ({}));
    return {
      ok: false,
      status: response.status,
      error: (body as { error?: string }).error || `HTTP ${response.status}`,
    };
  } catch (error) {
    return { ok: false, status: 0, error: error instanceof Error ? error.message : "Netzwerkfehler" };
  }
}

export function buildMailtoHref(
  subjectTemplate: string,
  bodyTemplate: string,
  company: string
): string {
  const companyLabel = company.trim() || "(Unternehmen bitte ergänzen)";
  const subject = subjectTemplate.replace("{company}", companyLabel);
  const body = bodyTemplate.replace("{company}", companyLabel);
  return `mailto:hallo@dbi-analytics.de?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
